import { MessageType, showMessage } from '@/shared';
import type { ICellRendererParams } from '@ag-grid-community/core';
import type { DocumentRelationExpandOpts, IDocument, IDocumentFolder } from '@condo/domain';
import * as Sentry from '@sentry/vue';

export const OFFICE_DOCUMENT_EXTENSIONS = ['pptx', 'ppt', 'docx', 'doc', 'xlsx', 'xls'] as const;

export interface IPresentationDocumentAndFolders extends IDocument {
    icon?: string;
    toolTip?: string;
    viewInModal?: boolean;
    audioPlayer?: boolean;
    modalIcon?: string;
    type?: 'document' | 'folder';
    /*Folders Part*/
    gridId?: number; //to maintain mix of records
    filePath?: string[]; //path to create grid grouping for tree view
    folderIdOrDocumentId?: number; //ID of folder or document
    folderId?: number; // folder ID
    isEditable: boolean;
}

/**
 * Create path in the structure of AgGrid Tree View
 */
function buildPath(entity: IDocumentFolder | IDocument, isFile = false, folders: IDocumentFolder[]) {
    const path = [];
    let currentFolderId = isFile ? entity.folderId : (entity as IDocumentFolder).parentFolderId;
    let entityName: string;
    let entityId: number;

    if ('filename' in entity) {
        entityName = entity.filename;
        entityId = entity.documentId;
    }
    if ('folderName' in entity) {
        entityName = entity.folderName;
        entityId = entity.folderId;
    }

    while (currentFolderId) {
        const folder = folders.find(folder => folder.folderId === currentFolderId);
        if (folder) {
            path.unshift(`${folder.folderName}_${folder.folderId}`);
            currentFolderId = folder.parentFolderId;
        } else {
            break;
        }
    }

    path.push(`${entityName}_${entityId}`);
    return path;
}

export const getFileCellRenderer = function getFileCellRenderer() {
    class FileCellRenderer {
        init(params: ICellRendererParams) {
            const tempDiv = document.createElement('div');
            const value = params.value;
            const icon = params.data.isExternalLink ? 'fa fa-link' : params.data?.mimeType ? `fa fa-${params.data.icon}` : 'fa fa-folder';
            tempDiv.innerHTML = icon ? '<span><i class="' + icon + '"></i>' + '<span class="filename"></span>' + value + '</span>' : value;
            //@ts-expect-error
            this.eGui = tempDiv.firstChild;
        }

        getGui() {
            //@ts-expect-error
            return this.eGui;
        }

        refresh() {
            return false;
        }
    }

    return FileCellRenderer;
};

function findCyclicDocumentFolder(folders: IDocumentFolder[]): IDocumentFolder | null {
    const folderMap = new Map<number, IDocumentFolder>();
    folders.forEach(folder => {
        folderMap.set(folder.folderId, folder);
    });

    for (const folder of folders) {
        const visited = new Set<number>();
        let current: IDocumentFolder | undefined = folder;

        while (current && current.parentFolderId) {
            if (visited.has(current.folderId)) {
                return current;
            }
            visited.add(current.folderId);
            current = folderMap.get(current.parentFolderId);
        }
    }

    return null;
}

export const prepareFilesForPresentation = (documents: IDocument[], folders: IDocumentFolder[] = []): IPresentationDocumentAndFolders[] => {
    // possibly detect cyclic folder that causes infinite rendering
    // https://linear.app/condo-group/issue/SKB-267
    const cyclicFolder = findCyclicDocumentFolder(folders);
    if (cyclicFolder) {
        Sentry.captureEvent(new Error(`Cycle detected in document folder ${cyclicFolder.folderId}`));
        showMessage('Cycles detected in the folder structure, cannot display documents. Please contact support.', MessageType.Danger);
        return [];
    }

    const folderRecords: IPresentationDocumentAndFolders[] = folders.map((f, index) => {
        return {
            filePath: buildPath(f, false, folders),
            gridId: `folder_${f.folderId}`,
            folderIdOrDocumentId: f.folderId,
            type: 'folder',
            isEditable: f.isEditable,
            ...f,
            mfhId: f.mfhId,
            estateId: f.estateId,
        } as unknown as IPresentationDocumentAndFolders;
    });

    const documentsProcessed: IPresentationDocumentAndFolders[] = documents.map(file => {
        const presentationDoc: IPresentationDocumentAndFolders = { ...file, viewInModal: false, type: 'document', isEditable: true };
        if (!presentationDoc.mimeType) {
            return presentationDoc;
        }
        const fileExtension = presentationDoc.filename.split('.').pop()?.toLowerCase() || '';
        const isOfficeDocument = OFFICE_DOCUMENT_EXTENSIONS.includes(fileExtension as (typeof OFFICE_DOCUMENT_EXTENSIONS)[number]);

        switch (true) {
            case isOfficeDocument:
                presentationDoc.viewInModal = true;
                if (['pptx', 'ppt'].includes(fileExtension)) {
                    presentationDoc.icon = 'file-powerpoint';
                    presentationDoc.modalIcon = 'file-powerpoint';
                    presentationDoc.toolTip = 'PowerPoint';
                } else if (['xlsx', 'xls'].includes(fileExtension)) {
                    presentationDoc.icon = 'file-excel';
                    presentationDoc.modalIcon = 'file-excel';
                    presentationDoc.toolTip = 'Excel';
                } else if (['docx', 'doc'].includes(fileExtension)) {
                    presentationDoc.icon = 'file-word';
                    presentationDoc.modalIcon = 'file-word';
                    presentationDoc.toolTip = 'Word';
                }
                break;
            case presentationDoc.mimeType.includes('image'):
                presentationDoc.icon = 'file-image';
                presentationDoc.viewInModal = true;
                presentationDoc.toolTip = 'Image';
                break;
            case presentationDoc.mimeType.includes('video'):
                presentationDoc.icon = 'file-video';
                presentationDoc.viewInModal = true;
                presentationDoc.toolTip = 'Video';
                break;
            case presentationDoc.mimeType.includes('pdf') || fileExtension === 'pdf':
                presentationDoc.icon = 'file-pdf';
                presentationDoc.toolTip = 'PDF';
                presentationDoc.viewInModal = true;
                presentationDoc.modalIcon = 'file-pdf';
                break;
            case presentationDoc.mimeType.includes('spreadsheet'):
                presentationDoc.icon = 'file-excel';
                presentationDoc.toolTip = 'Excel';
                break;
            case presentationDoc.mimeType.includes('officedocument'):
                presentationDoc.icon = 'file-word';
                presentationDoc.toolTip = 'MS Office';
                break;
            case presentationDoc.mimeType.includes('audio'):
                presentationDoc.icon = 'file-audio';
                presentationDoc.toolTip = 'Audio File';
                presentationDoc.audioPlayer = true;
                break;
            case presentationDoc.mimeType.includes('csv'):
                presentationDoc.icon = 'file-csv';
                presentationDoc.toolTip = 'CSV';
                break;
            case presentationDoc.filename.includes('.ifc'):
                presentationDoc.icon = 'cubes';
                presentationDoc.toolTip = 'IFC File';
                break;
            case presentationDoc.mimeType.includes('html') || presentationDoc.filename.includes('.html'):
                presentationDoc.icon = 'file-code';
                presentationDoc.toolTip = 'HTML';
                break;
            case presentationDoc.mimeType === 'text/plain' || fileExtension === 'txt':
                presentationDoc.icon = 'file-text';
                presentationDoc.toolTip = 'Text File';
                presentationDoc.viewInModal = true;
                break;
            default:
                presentationDoc.icon = 'file-alt';
                presentationDoc.toolTip = 'Other';
                break;
        }

        return {
            ...presentationDoc,
            estateId: presentationDoc?.documentRelation?.[0]?.estateId,
            mfhId: presentationDoc?.documentRelation?.[0]?.mfhId,
            filePath: buildPath(presentationDoc, true, folders),
            gridId: `document_${presentationDoc.documentId}`,
            folderIdOrDocumentId: presentationDoc.documentId,
            type: 'document',
        } as unknown as IPresentationDocumentAndFolders;
    });

    return [...folderRecords, ...documentsProcessed];
};

export const documentQueryExpandOpts: DocumentRelationExpandOpts = {
    select: ['documentRelationId', 'documentId', 'estateId', 'mfhId', 'rentTenancyId', 'investorInfoId'],
    expand: {
        document: {
            select: [
                'documentId',
                'uuid',
                'folderId',
                'mimeType',
                'documentCategory',
                'documentType',
                'filename',
                'createdAt',
                'link',
                'path',
                'isExternalLink',
            ],
        },
        rentTenancy: {
            select: ['rentTenancyId'],
            expand: {
                tenancyMembers: {
                    select: ['contactId', 'companyId', 'rentTenancyId'],
                    expand: {
                        contact: {
                            select: ['contactId', 'firstname', 'lastname'],
                        },
                        company: {
                            select: ['companyId', 'name', 'shortName'],
                        },
                    },
                },
            },
        },
        investorInfo: {
            select: ['investorInfoId'],
            expand: {
                investorInfoOwners: {
                    select: ['contactId', 'companyId', 'investorInfoId'],
                    expand: {
                        contact: {
                            select: ['contactId', 'firstname', 'lastname'],
                        },
                        company: {
                            select: ['companyId', 'name', 'shortName'],
                        },
                    },
                },
            },
        },
        rentingLead: {
            select: ['rentingLeadId'],
            expand: {
                contacts: {
                    select: ['contactId', 'firstname', 'lastname'],
                },
                companies: {
                    select: ['companyId', 'name', 'shortName'],
                },
            },
        },
    },
};
